import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

/** Mirrors boulder-patient's ContactMethod enum. */
export enum ContactMethod {
  PHONE = 'phoneNumber',
  EMAIL = 'emailAddress',
}

/**
 * Field names mirror the PotentialParticipant model in boulder-patient
 * (src/features/registration/stores/registration.ts) so the two codebases stay consistent.
 */
export type RegistrationData = {
  firstName?: string;
  lastName?: string;
  preferredName?: string;
  dob?: string;
  gender?: string;
  raceEthnicity: string[];
  pronouns?: string;
  phoneNumber?: string;
  emailAddress?: string;
  preferredContactMethod?: ContactMethod;
  communicationOptIn: boolean;
  consentToVoicemails: boolean;
};

export const DEFAULT_REGISTRATION_DATA: RegistrationData = {
  raceEthnicity: [],
  communicationOptIn: false,
  consentToVoicemails: false,
};

export type RegistrationDataContextValue = {
  registrationData: RegistrationData;
  /** Curried to match formControl.js's `setValue(value)` contract. */
  setRegistrationField: <K extends keyof RegistrationData>(
    key: K,
  ) => (value: RegistrationData[K]) => void;
};

const context = createContext<RegistrationDataContextValue | undefined>(undefined);

/**
 * In-memory only for now, by design: a full page load restarts the flow. Mount from wrapRootElement --
 * wrapPageElement remounts on every route change and would reset state between steps.
 */
export const RegistrationDataProvider = ({ children }: { children: React.ReactNode }) => {
  const [registrationData, setRegistrationData] =
    useState<RegistrationData>(DEFAULT_REGISTRATION_DATA);

  const setRegistrationField = useCallback(
    <K extends keyof RegistrationData>(key: K) =>
      (value: RegistrationData[K]) =>
        // A computed key off a generic widens to an index signature, hence the assertion.
        setRegistrationData((prev) => ({ ...prev, [key]: value } as RegistrationData)),
    [],
  );

  const value = useMemo<RegistrationDataContextValue>(
    () => ({ registrationData, setRegistrationField }),
    [registrationData, setRegistrationField],
  );

  return <context.Provider value={value}>{children}</context.Provider>;
};

export function useRegistrationData(): RegistrationDataContextValue {
  const value = useContext(context);

  if (!value) {
    throw new Error('useRegistrationData must be used inside <RegistrationDataProvider>.');
  }

  return value;
}
